/**
 * Focus and Blur Event Handlers
 * 
 * Manages field activation and deactivation events. Handles validation
 * timing, final formatting application, error state management, and
 * accessibility support.
 */

import { FieldType } from '../../detection-patterns';
import { EventContext, EventProcessingResult, EventHandler } from '../event-manager';

/**
 * Focus/Blur processing configuration
 */
export interface FocusBlurConfig {
  /** Whether to apply final formatting on blur */
  formatOnBlur: boolean;
  /** Whether to validate on blur */
  validateOnBlur: boolean;
  /** Whether to clear error states on focus */
  clearErrorsOnFocus: boolean;
  /** Whether to show field hints on focus */
  showHintsOnFocus: boolean;
  /** Whether to handle mobile keyboard optimization */
  mobileOptimization: boolean;
  /** Custom CSS classes for focus/blur states */
  cssClasses?: {
    focused?: string;
    blurred?: string;
    valid?: string;
    invalid?: string;
    error?: string;
  };
  /** Whether to update field registry states */
  updateRegistryState: boolean;
}

/**
 * Field state information
 */
export interface FieldState {
  /** Whether the field is currently focused */
  isFocused: boolean;
  /** Whether the field has been touched (focused at least once) */
  wasTouched: boolean;
  /** Whether the field is valid */
  isValid?: boolean;
  /** Current error messages */
  errors?: string[];
  /** Whether formatting has been applied */
  isFormatted?: boolean;
  /** Original value when focus was gained */
  originalFocusValue?: string;
  /** Timestamp when focus was gained */
  focusTimestamp?: number;
  /** Timestamp when blur occurred */
  blurTimestamp?: number;
}

/**
 * Default configurations for different field types
 */
const DEFAULT_FOCUS_BLUR_CONFIGS: Record<FieldType, FocusBlurConfig> = {
  [FieldType.PHONE]: {
    formatOnBlur: true,
    validateOnBlur: true,
    clearErrorsOnFocus: true,
    showHintsOnFocus: true,
    mobileOptimization: true,
    updateRegistryState: true,
    cssClasses: {
      focused: 'field-focused',
      valid: 'field-valid',
      invalid: 'field-invalid'
    }
  },
  [FieldType.ZIP]: {
    formatOnBlur: true,
    validateOnBlur: true,
    clearErrorsOnFocus: true,
    showHintsOnFocus: true,
    mobileOptimization: true,
    updateRegistryState: true,
    cssClasses: {
      focused: 'field-focused',
      valid: 'field-valid',
      invalid: 'field-invalid'
    }
  },
  [FieldType.DATE_OF_BIRTH]: {
    formatOnBlur: true,
    validateOnBlur: true,
    clearErrorsOnFocus: true,
    showHintsOnFocus: true,
    mobileOptimization: true,
    updateRegistryState: true,
    cssClasses: {
      focused: 'field-focused',
      valid: 'field-valid',
      invalid: 'field-invalid'
    }
  },
  [FieldType.CURRENCY]: {
    formatOnBlur: true,
    validateOnBlur: true,
    clearErrorsOnFocus: true,
    showHintsOnFocus: true,
    mobileOptimization: true,
    updateRegistryState: true,
    cssClasses: {
      focused: 'field-focused',
      valid: 'field-valid',
      invalid: 'field-invalid'
    }
  },
  [FieldType.SSN]: {
    formatOnBlur: true, // SSN gets masked on blur for security
    validateOnBlur: true,
    clearErrorsOnFocus: false, // Don't clear SSN errors immediately
    showHintsOnFocus: true,
    mobileOptimization: true,
    updateRegistryState: true,
    cssClasses: {
      focused: 'field-focused ssn-field',
      valid: 'field-valid',
      invalid: 'field-invalid'
    }
  },
  [FieldType.UNKNOWN]: {
    formatOnBlur: false,
    validateOnBlur: false,
    clearErrorsOnFocus: true,
    showHintsOnFocus: false,
    mobileOptimization: true,
    updateRegistryState: true
  }
};

/**
 * Field states storage (WeakMap for memory efficiency)
 */
const fieldStates = new WeakMap<HTMLElement, FieldState>();

/**
 * Create a focus event handler for a specific field type
 * 
 * @param fieldType The type of field to handle
 * @param customConfig Optional configuration overrides
 * @returns Event handler function
 */
export function createFocusHandler(
  fieldType: FieldType,
  customConfig?: Partial<FocusBlurConfig>
): EventHandler {
  const config = { ...DEFAULT_FOCUS_BLUR_CONFIGS[fieldType], ...customConfig };

  return async (context: EventContext): Promise<EventProcessingResult> => {
    const startTime = performance.now();
    const { element, field, value, isMobile } = context;

    try {
      // Get or create field state
      let state = fieldStates.get(element);
      if (!state) {
        state = {
          isFocused: false,
          wasTouched: false
        };
        fieldStates.set(element, state);
      }

      // Update state
      state.isFocused = true;
      state.wasTouched = true;
      state.originalFocusValue = value;
      state.focusTimestamp = Date.now();

      // Clear error states if configured
      if (config.clearErrorsOnFocus && state.errors && state.errors.length > 0) {
        state.errors = [];
        clearFieldErrors(element, config);
      }

      // Apply focus CSS class
      if (config.cssClasses?.focused) {
        element.classList.add(config.cssClasses.focused);
      }

      // Remove blur/error classes
      if (config.cssClasses?.blurred) {
        element.classList.remove(config.cssClasses.blurred);
      }
      if (config.cssClasses?.invalid) {
        element.classList.remove(config.cssClasses.invalid);
      }

      // Update field registry state
      if (config.updateRegistryState) {
        // This will be handled by the integration layer
        // For now, we just track the state locally
      }

      // Show field hints if configured
      if (config.showHintsOnFocus) {
        showFieldHint(element, fieldType, isMobile);
      }

      // Mobile optimization
      if (config.mobileOptimization && isMobile) {
        optimizeMobileInput(element, fieldType);
      }

      // Apply accessibility enhancements
      enhanceAccessibility(element, fieldType, 'focus');

      return {
        success: true,
        processingTime: performance.now() - startTime
      };

    } catch (error) {
      return {
        success: false,
        errors: [`Focus processing error: ${error}`],
        processingTime: performance.now() - startTime
      };
    }
  };
}

/**
 * Create a blur event handler for a specific field type
 * 
 * @param fieldType The type of field to handle
 * @param customConfig Optional configuration overrides
 * @returns Event handler function
 */
export function createBlurHandler(
  fieldType: FieldType,
  customConfig?: Partial<FocusBlurConfig>
): EventHandler {
  const config = { ...DEFAULT_FOCUS_BLUR_CONFIGS[fieldType], ...customConfig };

  return async (context: EventContext): Promise<EventProcessingResult> => {
    const startTime = performance.now();
    const { element, field, value } = context;

    try {
      // Get field state
      let state = fieldStates.get(element);
      if (!state) {
        state = {
          isFocused: true, // Was focused if blur is firing
          wasTouched: true
        };
        fieldStates.set(element, state);
      }

      // Update state
      state.isFocused = false;
      state.blurTimestamp = Date.now();

      // Remove focus CSS class and add blur class
      if (config.cssClasses?.focused) {
        element.classList.remove(config.cssClasses.focused);
      }
      if (config.cssClasses?.blurred) {
        element.classList.add(config.cssClasses.blurred);
      }

      let finalValue = value;
      let formatApplied = false;
      let errors: string[] = [];

      // Apply final formatting if configured and value has content
      if (config.formatOnBlur && finalValue.trim().length > 0) {
        const formatResult = await applyFinalFormatting(finalValue, fieldType);
        if (formatResult.success && formatResult.formattedValue !== finalValue) {
          finalValue = formatResult.formattedValue;
          formatApplied = true;
          state.isFormatted = true;
        }
        if (formatResult.errors) {
          errors.push(...formatResult.errors);
        }
      }

      // Apply field masking for sensitive fields
      if (fieldType === FieldType.SSN && finalValue.trim().length > 0) {
        const maskResult = await applySSNMasking(finalValue, element);
        if (maskResult.success) {
          finalValue = maskResult.maskedValue;
          formatApplied = true;
        }
      }

      // Validate if configured and value has content
      if (config.validateOnBlur && finalValue.trim().length > 0) {
        const validationResult = await validateFieldOnBlur(finalValue, fieldType, state);
        state.isValid = validationResult.isValid;
        if (validationResult.errors && validationResult.errors.length > 0) {
          errors.push(...validationResult.errors);
          state.errors = validationResult.errors;
        }
      }

      // Apply validation CSS classes
      if (config.cssClasses) {
        if (state.isValid === true && config.cssClasses.valid) {
          element.classList.add(config.cssClasses.valid);
          element.classList.remove(config.cssClasses.invalid || '');
        } else if (state.isValid === false && config.cssClasses.invalid) {
          element.classList.add(config.cssClasses.invalid);
          element.classList.remove(config.cssClasses.valid || '');
        }
      }

      // Show validation errors if any
      if (errors.length > 0) {
        showFieldErrors(element, errors, config);
      } else {
        clearFieldErrors(element, config);
      }

      // Hide field hints
      hideFieldHint(element);

      // Update field registry state
      if (config.updateRegistryState) {
        // This will be handled by the integration layer
        // For now, we just track the state locally
      }

      // Apply accessibility enhancements
      enhanceAccessibility(element, fieldType, 'blur', state.isValid, errors);

      return {
        success: true,
        newValue: finalValue !== value ? finalValue : undefined,
        formatApplied,
        errors: errors.length > 0 ? errors : undefined,
        processingTime: performance.now() - startTime
      };

    } catch (error) {
      return {
        success: false,
        errors: [`Blur processing error: ${error}`],
        processingTime: performance.now() - startTime
      };
    }
  };
}

/**
 * Apply final formatting on blur
 */
async function applyFinalFormatting(
  value: string,
  fieldType: FieldType
): Promise<{
  success: boolean;
  formattedValue: string;
  errors?: string[];
}> {
  try {
    let formattedValue = value;

    switch (fieldType) {
      case FieldType.PHONE:
        const phoneDigits = value.replace(/\D/g, '');
        if (phoneDigits.length === 10) {
          formattedValue = `(${phoneDigits.slice(0, 3)}) ${phoneDigits.slice(3, 6)}-${phoneDigits.slice(6)}`;
        }
        break;

      case FieldType.ZIP:
        const zipDigits = value.replace(/\D/g, '');
        if (zipDigits.length === 9) {
          formattedValue = `${zipDigits.slice(0, 5)}-${zipDigits.slice(5)}`;
        } else if (zipDigits.length === 5) {
          formattedValue = zipDigits;
        }
        break;

      case FieldType.DATE_OF_BIRTH:
        const dateDigits = value.replace(/\D/g, '');
        if (dateDigits.length === 8) {
          formattedValue = `${dateDigits.slice(0, 2)}/${dateDigits.slice(2, 4)}/${dateDigits.slice(4)}`;
        }
        break;

      case FieldType.CURRENCY:
        const numericValue = value.replace(/[^\d.]/g, '');
        const num = parseFloat(numericValue);
        if (!isNaN(num)) {
          formattedValue = `$${num.toLocaleString('en-US', { 
            minimumFractionDigits: 2, 
            maximumFractionDigits: 2 
          })}`;
        }
        break;

      default:
        break;
    }

    return {
      success: true,
      formattedValue
    };

  } catch (error) {
    return {
      success: false,
      formattedValue: value,
      errors: [`Formatting error: ${error}`]
    };
  }
}

/**
 * Apply SSN masking for security
 */
async function applySSNMasking(
  value: string,
  element: HTMLElement
): Promise<{
  success: boolean;
  maskedValue: string;
  errors?: string[];
}> {
  try {
    const digits = value.replace(/\D/g, '');
    
    if (digits.length === 9) {
      // Store original value in data attribute for form submission
      element.setAttribute('data-original-value', digits);
      
      // Return masked display value (show only last 4 digits)
      const maskedValue = `XXX-XX-${digits.slice(5)}`;
      
      return {
        success: true,
        maskedValue
      };
    }

    return {
      success: false,
      maskedValue: value,
      errors: ['Invalid SSN format']
    };

  } catch (error) {
    return {
      success: false,
      maskedValue: value,
      errors: [`SSN masking error: ${error}`]
    };
  }
}

/**
 * Validate field on blur
 */
async function validateFieldOnBlur(
  value: string,
  fieldType: FieldType,
  state: FieldState
): Promise<{
  isValid: boolean;
  errors?: string[];
}> {
  const errors: string[] = [];

  try {
    switch (fieldType) {
      case FieldType.PHONE:
        const phoneDigits = value.replace(/\D/g, '');
        if (phoneDigits.length !== 10) {
          errors.push('Phone number must be 10 digits');
        }
        break;

      case FieldType.ZIP:
        const zipDigits = value.replace(/\D/g, '');
        if (zipDigits.length !== 5 && zipDigits.length !== 9) {
          errors.push('ZIP code must be 5 or 9 digits');
        }
        break;

      case FieldType.DATE_OF_BIRTH:
        const dateMatch = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (dateMatch) {
          const [, month, day, year] = dateMatch;
          const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
          const today = new Date();
          
          if (date > today) {
            errors.push('Date of birth cannot be in the future');
          } else if (today.getFullYear() - date.getFullYear() > 120) {
            errors.push('Date of birth seems too old');
          }
        } else {
          errors.push('Date must be in MM/DD/YYYY format');
        }
        break;

      case FieldType.CURRENCY:
        const numericValue = value.replace(/[^\d.]/g, '');
        const num = parseFloat(numericValue);
        if (isNaN(num) || num < 0) {
          errors.push('Please enter a valid currency amount');
        }
        break;

      case FieldType.SSN:
        const ssnDigits = value.replace(/\D/g, '');
        if (ssnDigits.length !== 9) {
          errors.push('SSN must be 9 digits');
        } else {
          // Basic SSN validation (not starting with 000, 666, or 9XX)
          const firstThree = ssnDigits.substring(0, 3);
          if (firstThree === '000' || firstThree === '666' || firstThree.startsWith('9')) {
            errors.push('Invalid SSN format');
          }
        }
        break;

      default:
        break;
    }

    return {
      isValid: errors.length === 0,
      errors: errors.length > 0 ? errors : undefined
    };

  } catch (error) {
    return {
      isValid: false,
      errors: [`Validation error: ${error}`]
    };
  }
}

/**
 * Show field hints on focus
 */
function showFieldHint(element: HTMLElement, fieldType: FieldType, isMobile?: boolean): void {
  const hints: Record<FieldType, string> = {
    [FieldType.PHONE]: 'Format: (XXX) XXX-XXXX',
    [FieldType.ZIP]: 'Format: XXXXX or XXXXX-XXXX',
    [FieldType.DATE_OF_BIRTH]: 'Format: MM/DD/YYYY',
    [FieldType.CURRENCY]: 'Format: $X,XXX.XX',
    [FieldType.SSN]: 'Format: XXX-XX-XXXX',
    [FieldType.UNKNOWN]: ''
  };

  const hint = hints[fieldType];
  if (!hint) return;

  // Create or update hint element
  let hintElement = element.parentElement?.querySelector('.field-hint') as HTMLElement;
  if (!hintElement) {
    hintElement = document.createElement('div');
    hintElement.className = 'field-hint';
    hintElement.setAttribute('aria-live', 'polite');
    element.parentElement?.appendChild(hintElement);
  }

  hintElement.textContent = hint;
  hintElement.style.display = 'block';
}

/**
 * Hide field hints
 */
function hideFieldHint(element: HTMLElement): void {
  const hintElement = element.parentElement?.querySelector('.field-hint') as HTMLElement;
  if (hintElement) {
    hintElement.style.display = 'none';
  }
}

/**
 * Show field errors
 */
function showFieldErrors(element: HTMLElement, errors: string[], config: FocusBlurConfig): void {
  // Create or update error element
  let errorElement = element.parentElement?.querySelector('.field-error') as HTMLElement;
  if (!errorElement) {
    errorElement = document.createElement('div');
    errorElement.className = 'field-error';
    errorElement.setAttribute('role', 'alert');
    errorElement.setAttribute('aria-live', 'assertive');
    element.parentElement?.appendChild(errorElement);
  }

  errorElement.textContent = errors[0]; // Show first error
  errorElement.style.display = 'block';

  // Add error CSS class
  if (config.cssClasses?.error) {
    element.classList.add(config.cssClasses.error);
  }

  // Update aria-describedby for accessibility
  const errorId = `${element.id || 'field'}-error`;
  errorElement.id = errorId;
  element.setAttribute('aria-describedby', errorId);
  element.setAttribute('aria-invalid', 'true');
}

/**
 * Clear field errors
 */
function clearFieldErrors(element: HTMLElement, config: FocusBlurConfig): void {
  const errorElement = element.parentElement?.querySelector('.field-error') as HTMLElement;
  if (errorElement) {
    errorElement.style.display = 'none';
    errorElement.textContent = '';
  }

  // Remove error CSS class
  if (config.cssClasses?.error) {
    element.classList.remove(config.cssClasses.error);
  }

  // Clear accessibility attributes
  element.removeAttribute('aria-describedby');
  element.setAttribute('aria-invalid', 'false');
}

/**
 * Optimize mobile input
 */
function optimizeMobileInput(element: HTMLElement, fieldType: FieldType): void {
  const input = element as HTMLInputElement;
  if (!input.setAttribute) return;

  // Set input type and attributes for better mobile keyboards
  switch (fieldType) {
    case FieldType.PHONE:
      input.setAttribute('inputmode', 'tel');
      input.setAttribute('autocomplete', 'tel');
      break;

    case FieldType.ZIP:
      input.setAttribute('inputmode', 'numeric');
      input.setAttribute('autocomplete', 'postal-code');
      break;

    case FieldType.DATE_OF_BIRTH:
      input.setAttribute('inputmode', 'numeric');
      input.setAttribute('autocomplete', 'bday');
      break;

    case FieldType.CURRENCY:
      input.setAttribute('inputmode', 'decimal');
      input.setAttribute('autocomplete', 'transaction-amount');
      break;

    case FieldType.SSN:
      input.setAttribute('inputmode', 'numeric');
      input.setAttribute('autocomplete', 'off');
      break;

    default:
      break;
  }
}

/**
 * Enhance accessibility
 */
function enhanceAccessibility(
  element: HTMLElement,
  fieldType: FieldType,
  eventType: 'focus' | 'blur',
  isValid?: boolean,
  errors?: string[]
): void {
  // Update ARIA attributes
  if (eventType === 'focus') {
    element.setAttribute('aria-expanded', 'false');
  }

  if (eventType === 'blur') {
    if (isValid !== undefined) {
      element.setAttribute('aria-invalid', isValid ? 'false' : 'true');
    }

    if (errors && errors.length > 0) {
      element.setAttribute('aria-describedby', `${element.id || 'field'}-error`);
    }
  }

  // Add field type information for screen readers
  if (!element.getAttribute('aria-label') && !element.getAttribute('aria-labelledby')) {
    const fieldTypeLabels: Record<FieldType, string> = {
      [FieldType.PHONE]: 'Phone number',
      [FieldType.ZIP]: 'ZIP code',
      [FieldType.DATE_OF_BIRTH]: 'Date of birth',
      [FieldType.CURRENCY]: 'Currency amount',
      [FieldType.SSN]: 'Social Security Number',
      [FieldType.UNKNOWN]: 'Text input'
    };

    const label = fieldTypeLabels[fieldType];
    if (label) {
      element.setAttribute('aria-label', label);
    }
  }
}

/**
 * Get field state for external access
 */
export function getFieldState(element: HTMLElement): FieldState | undefined {
  return fieldStates.get(element);
}

/**
 * Set field state (for testing or external control)
 */
export function setFieldState(element: HTMLElement, state: Partial<FieldState>): void {
  let currentState = fieldStates.get(element);
  if (!currentState) {
    currentState = {
      isFocused: false,
      wasTouched: false
    };
    fieldStates.set(element, currentState);
  }

  Object.assign(currentState, state);
}

/**
 * Clear all field states (for cleanup)
 */
export function clearAllFieldStates(): void {
  // WeakMap doesn't have a clear method, but it will be garbage collected
  // when elements are removed from the DOM
}

/**
 * Get default focus/blur configuration for field type
 */
export function getDefaultFocusBlurConfig(fieldType: FieldType): FocusBlurConfig {
  return { ...DEFAULT_FOCUS_BLUR_CONFIGS[fieldType] };
}